import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { toast } from "sonner";
import { renderMarkdown } from "../lib/markdown";
import DOMPurify from "dompurify";
import { Sparkles, ArrowLeft, Copy, Download, Loader2, Play, Pause, Volume2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth } from "../lib/auth";

export default function DocumentDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [voice, setVoice] = useState("nova");
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [playing, setPlaying] = useState(false);
  const audioRef = React.useRef(null);

  useEffect(() => {
    if (!id) return;
    api.get(`/api/documents/${id}`).then((r) => setDoc(r.data));
  }, [id]);

  useEffect(() => () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
  }, [audioUrl]);

  const listen = async () => {
    if (audioUrl) {
      if (audioRef.current) {
        playing ? audioRef.current.pause() : audioRef.current.play();
      }
      return;
    }
    setAudioLoading(true);
    try {
      const { data } = await api.post("/api/tts/synthesize", { doc_id: id, voice });
      const audioResp = await api.get(data.url, { responseType: "blob" });
      const url = URL.createObjectURL(audioResp.data);
      setAudioUrl(url);
      // auto-play on first load
      setTimeout(() => audioRef.current?.play(), 100);
      toast.success(data.cached ? "Loaded from cache" : `${data.chars} characters spoken`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "TTS failed");
    } finally {
      setAudioLoading(false);
    }
  };

  const copy = () => { navigator.clipboard.writeText(doc.content); toast.success("Copied to clipboard"); };
  const download = () => {
    const blob = new Blob([doc.content], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${doc.title.replace(/[^a-z0-9]+/gi, "_")}.md`; a.click();
    URL.revokeObjectURL(url);
  };

  if (!doc) return <div className="p-16 text-center text-neutral-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-4xl mx-auto px-5 lg:px-8 py-10">
        <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900 mb-6" data-testid="doc-back">
          <ArrowLeft size={16} /> Back
        </button>
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <div className="overline">GENERATED DOCUMENT</div>
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">{doc.title}</h1>
            <div className="text-xs text-neutral-500 mt-2">{new Date(doc.created_at).toLocaleString()}</div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <div className="flex items-center gap-2 border border-neutral-200 rounded-full pl-3 pr-1 py-1" data-testid="listen-controls">
              <Volume2 size={14} className="text-neutral-500" />
              <Select value={voice} onValueChange={setVoice}>
                <SelectTrigger className="h-8 w-32 border-0 shadow-none text-xs" data-testid="voice-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="nova">Nova · upbeat</SelectItem>
                  <SelectItem value="alloy">Alloy · balanced</SelectItem>
                  <SelectItem value="onyx">Onyx · deep</SelectItem>
                  <SelectItem value="shimmer">Shimmer · bright</SelectItem>
                  <SelectItem value="fable">Fable · storytelling</SelectItem>
                  <SelectItem value="echo">Echo · calm</SelectItem>
                </SelectContent>
              </Select>
              <button onClick={listen} disabled={audioLoading}
                className="h-8 px-3 rounded-full bg-neutral-900 text-white text-xs font-medium flex items-center gap-1 hover:bg-neutral-800 disabled:opacity-60"
                data-testid="listen-btn">
                {audioLoading ? <Loader2 size={12} className="animate-spin" /> : (playing ? <Pause size={12} /> : <Play size={12} />)}
                {audioLoading ? "Loading" : (playing ? "Pause" : audioUrl ? "Play" : "Listen")}
              </button>
            </div>
            <button onClick={copy} className="btn-ghost text-sm" data-testid="doc-copy"><Copy size={14} /> Copy</button>
            <button onClick={download} className="btn-primary text-sm" data-testid="doc-download"><Download size={14} /> Download .md</button>
          </div>
        </div>
        {audioUrl && (
          <audio ref={audioRef} src={audioUrl}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onEnded={() => setPlaying(false)}
            controls className="w-full mb-4" data-testid="audio-player" />
        )}
        <div className="gf-card p-8 prose-doc" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderMarkdown(doc.content), { USE_PROFILES: { html: true } }) }} data-testid="doc-content" />
      </div>
    </div>
  );
}

export function DocumentGenerator() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [docTypes, setDocTypes] = useState([]);
  const [grants, setGrants] = useState([]);
  const [docType, setDocType] = useState("business_plan");
  const [grantId, setGrantId] = useState("");
  const [ctx, setCtx] = useState("");
  const [loading, setLoading] = useState(false);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/api/documents/types").then((r) => setDocTypes(r.data.types));
    api.get("/api/grants/", { params: { limit: 60 } }).then((r) => setGrants(r.data));
    api.get("/api/documents/").then((r) => setRecent(r.data));
    // Query param for preselected grant
    const params = new URLSearchParams(window.location.search);
    if (params.get("grant")) setGrantId(params.get("grant"));
  }, []);

  const generate = async () => {
    if (user?.subscription_status !== "active") {
      nav("/pricing");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/api/documents/generate", {
        doc_type: docType, grant_id: grantId || null, additional_context: ctx,
      });
      toast.success("Document generated");
      nav(`/documents/${data.id}`);
    } catch (e) {
      if (e.response?.status === 402) { nav("/pricing"); return; }
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-10 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="overline mb-2">AI DOCUMENT GENERATOR</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Draft it. Ship it. Win it.</h1>
          <p className="text-neutral-600 mt-2">Powered by Claude Sonnet 5. Grounded in your organization profile and the specific grant.</p>

          <div className="gf-card p-6 mt-8" data-testid="generator-form">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Document type</label>
                <Select value={docType} onValueChange={setDocType}>
                  <SelectTrigger className="mt-1 h-11" data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {docTypes.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Grant (optional)</label>
                <Select value={grantId || "none"} onValueChange={(v) => setGrantId(v === "none" ? "" : v)}>
                  <SelectTrigger className="mt-1 h-11" data-testid="grant-select"><SelectValue placeholder="No specific grant" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="none">No specific grant</SelectItem>
                    {grants.map((g) => <SelectItem key={g.id} value={g.id}>{g.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4">
              <label className="text-sm font-medium">Extra context (optional)</label>
              <textarea rows={4} value={ctx} onChange={(e) => setCtx(e.target.value)}
                placeholder="Specific angle, program name, target population, key metrics you want highlighted..."
                className="mt-1 w-full border border-neutral-200 rounded-lg p-3" data-testid="doc-context" />
            </div>
            <div className="mt-6 flex items-center gap-3">
              <button onClick={generate} disabled={loading}
                className="btn-primary" data-testid="doc-generate-btn">
                {loading ? <><Loader2 size={16} className="animate-spin" /> Drafting…</> : <><Sparkles size={16} /> Generate document</>}
              </button>
              {user?.subscription_status !== "active" && (
                <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1.5" data-testid="upgrade-hint">
                  Pro plan required — you'll be redirected to upgrade
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="overline mb-2">RECENT DOCUMENTS</div>
          <h2 className="font-display text-2xl font-semibold mb-4">Your library</h2>
          {recent.length === 0 ? (
            <div className="border border-dashed border-neutral-300 rounded-xl p-6 text-sm text-neutral-500 text-center">Your drafts will appear here.</div>
          ) : (
            <ul className="space-y-3">
              {recent.slice(0, 10).map((d) => (
                <li key={d.id} className="gf-card p-4" data-testid={`recent-doc-${d.id}`}>
                  <Link to={`/documents/${d.id}`} className="block">
                    <div className="font-medium line-clamp-2">{d.title}</div>
                    <div className="text-xs text-neutral-500 mt-1">{new Date(d.created_at).toLocaleDateString()}</div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
